import qs from 'qs';
import z from 'zod';
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from "../app/lib/filters/dto";
import { JobResponse } from '../app/lib/polling/dto';
import { contactsFiltersAdapter } from '../app/lib/utils';
import { CompanyDTO } from '../app/pages/database/companies/dto';
import { newEmployeeSchema } from '../app/pages/database/companies/editCompany/employees/validation';
import { AssociatePersonToCompany, PersonDTO } from '../app/pages/database/people/dto';
import { updatePersonSchema } from '../app/pages/database/people/editPerson/validation';
import { baseApi } from "./_baseQuery";

export const peopleApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAllPeople: build.query<{ people: FindContactsDatabaseResponse<ContactType.PERSON>, toActivate: number }, FindAllContactsFilters>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const peopleResponse = await fetchWithBQ(
                        {
                            url: "/person",
                            params: qs.stringify({ ...contactsFiltersAdapter(_arg, ContactType.PERSON) }, {
                                arrayFormat: "repeat",
                                skipNulls: true,
                                allowEmptyArrays: false,
                                encode: true,
                            })
                        }
                    );
                    if (peopleResponse.error) return { error: peopleResponse.error };

                    const peopleToActivateCount = await fetchWithBQ("/person/count");
                    if (peopleToActivateCount.error) return { error: peopleToActivateCount.error };

                    const toActivateCount = peopleToActivateCount.data as { toActivate: number };

                    return {
                        data: {
                            people: peopleResponse.data as FindContactsDatabaseResponse<ContactType.PERSON>,
                            toActivate: toActivateCount.toActivate
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch users or avatars',
                            data: error
                        }
                    };
                }
            },
            providesTags: [{ type: 'Database', id: 'people' }]
        }),
        findOnePerson: build.query<{ person: PersonDTO, associatedCompany: CompanyDTO | null }, string>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const personRes = await fetchWithBQ(`person/${id}`);
                    if (personRes.error) return { error: personRes.error };

                    const person = personRes.data as PersonDTO;

                    let associatedCompany: CompanyDTO | null = null;
                    if (person.companyId) {
                        const companyRes = await fetchWithBQ("/companies/" + person.companyId);
                        if (!companyRes.error) {
                            associatedCompany = companyRes.data as CompanyDTO;
                        }
                    }

                    return {
                        data: {
                            person,
                            associatedCompany
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch person or company',
                            data: error
                        }
                    };
                }
            },
            providesTags: (_result, _error, id) => [
                { type: 'People', id },
                ...(_result?.person.companyId ? [{ type: 'Companies' as const, id: _result.person.companyId }] : []),
            ],

        }),
        createPerson: build.mutation<{
            success: boolean; response: {
                id: string,
                creationStatus: {
                    creationState: string
                }
            }
        }, { data: z.infer<typeof newEmployeeSchema>, companyId: string, addToTarget?: string }>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const response = await fetchWithBQ({
                        url: `/person`,
                        method: "POST",
                        body: { ..._arg.data, companyId: _arg.companyId },
                    });
                    if (response.error) return { error: response.error };

                    const responseData = response.data as {
                        response: {
                            id: string,
                            creationStatus: {
                                creationState: string
                            }
                        }
                    };

                    if (responseData !== null && _arg.addToTarget) {
                        const addInTargetResponse = await fetchWithBQ({
                            url: `/contacts/bulk`,
                            method: "POST",
                            body: {
                                targetListId: _arg.addToTarget,
                                contactIds: [responseData?.response.id],
                                contactType: ContactType.PERSON
                            }
                        });
                        if (addInTargetResponse.error) {
                            return { error: addInTargetResponse.error };
                        }
                    }

                    return {
                        data: response.data as {
                            success: boolean; response: {
                                id: string,
                                creationStatus: {
                                    creationState: string
                                }
                            }
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to create person',
                            data: error
                        }
                    };
                }
            },
            invalidatesTags: (_result, _error, { companyId, addToTarget }) => [{ type: 'People', id: _result?.response?.id }, { type: 'Database', id: 'people' }, { type: 'Companies', id: companyId || '' }],
        }),
        updatePerson: build.mutation<{ success: boolean; person: PersonDTO }, { id: string, data: z.infer<typeof updatePersonSchema> }>({
            query: ({ id, data }) => ({
                url: `/person/${id}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, { id, data }) => [{ type: 'People', id }, { type: 'Database', id: 'people' }, { type: 'Companies', id: _result?.person.companyId || '' }],
        }),
        updatePrivacy: build.mutation<{ success: boolean; person: PersonDTO }, { id: string }>({
            query: ({ id }) => ({
                url: `/person/${id}`,
                method: "PUT",
                body: { privacy: true },
            }),
            invalidatesTags: (_result, _error, { id }) => [{ type: 'People', id }, { type: 'Database', id: 'people' }],
        }),
        removePerson: build.mutation<{ success: boolean; id: string }, { ids: string[], companyId?: string }>({
            query: (ids) => ({
                url: `person`,
                method: 'DELETE',
                body: ids.ids
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'people' }, { type: 'Companies', id: _ids.companyId || '' }],
        }),
        restorePerson: build.mutation<{ success: boolean; }, { ids: string[] }>({
            query: (ids) => ({
                url: `person/restore`,
                method: 'PUT',
                body: ids
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'people' }],
        }),
        removeBulkPeople: build.mutation<JobResponse, FindAllContactsFilters>({
            query: (filters) => ({
                url: `person/bulk-delete`,
                method: 'POST',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.PERSON) }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
            invalidatesTags: (_result, _error, _ids) => [{ type: 'Database', id: 'people' }],
        }),
        importPeopleFromCSV: build.mutation<JobResponse, FormData>({
            query: (body) => ({
                url: `person/csv`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Database', id: 'people' }],
        }),
        getPeopleCSV: build.mutation<JobResponse, string[]>({
            query: (ids) => ({
                url: `person/export-csv`,
                method: 'POST',
                body: { ids }
            }),
        }),
        bulkPeopleCSV: build.mutation<JobResponse, FindAllContactsFilters>({
            query: (filters) => ({
                url: `person/bulk-csv`,
                method: 'POST',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.PERSON) }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
        }),
        associateToCompany: build.mutation<void, { personId: string, data: AssociatePersonToCompany }>({
            query: ({ personId, data }) => ({
                url: `/person/${personId}/company`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: (_result, _error, { personId }) => [{ type: 'People', id: personId }, { type: 'Database', id: 'people' }],
        }),
        dissociateEmployee: build.mutation<void, { personId: string, data: AssociatePersonToCompany | null, oldCompanyId?: string | null }>({
            query: ({ personId, data }) => {
                const baseQuery = {
                    url: `/person/${personId}/company`,
                    method: 'PUT' as const,
                    body: {}
                }
                return data ? { ...baseQuery, body: data } : baseQuery
            },
            invalidatesTags: (_result, _error, { personId, oldCompanyId }) => [{ type: 'People', id: personId }, { type: 'Database', id: 'people' }, { type: 'Companies', id: oldCompanyId ?? undefined }],
        })
    }),
})

export const {
    useFindAllPeopleQuery,
    useRemovePersonMutation,
    useRemoveBulkPeopleMutation,
    useBulkPeopleCSVMutation,
    useGetPeopleCSVMutation,
    useImportPeopleFromCSVMutation,
    useFindOnePersonQuery,
    useUpdatePersonMutation,
    useUpdatePrivacyMutation,
    useAssociateToCompanyMutation,
    useDissociateEmployeeMutation,
    useRestorePersonMutation,
    useCreatePersonMutation,
} = peopleApi;